import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { KeyRound, Trash2, AlertCircle, CheckCircle2, Layers, ShieldCheck, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { getBackendUrl } from '../../utils/api';

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

export const MasterAIKeyPoolContent = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [keysText, setKeysText] = useState('');
  const [showExpired, setShowExpired] = useState(false);

  const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const fetchPool = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/master/ai-key-pool`, authHeader());
      setData(res.data);
    } catch (e) {
      console.error('Erro ao carregar pool:', e);
      toast.error('Erro ao carregar o pool de chaves');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPool(); }, []);

  const lineCount = keysText.split('\n').map((l) => l.trim()).filter(Boolean).length;

  const handleSave = async () => {
    if (!keysText.trim()) { toast.error('Cole ao menos uma chave'); return; }
    try {
      setSaving(true);
      const res = await axios.post(`${API}/master/ai-key-pool`, { keys_text: keysText }, authHeader());
      const r = res.data || {};
      const parts = [`${r.added} adicionada(s)`];
      if (r.duplicates) parts.push(`${r.duplicates} duplicada(s)`);
      if (r.invalid) parts.push(`${r.invalid} inválida(s)`);
      toast.success(parts.join(' • '));
      setKeysText('');
      fetchPool();
    } catch (e) {
      console.error('Erro ao salvar chaves:', e);
      toast.error('Erro ao adicionar chaves');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (id) => {
    try {
      await axios.delete(`${API}/master/ai-key-pool/${id}`, authHeader());
      toast.success('Chave removida da fila');
      fetchPool();
    } catch (e) {
      console.error('Erro ao remover chave:', e);
      toast.error('Erro ao remover chave');
    }
  };

  const handleClearExpired = async () => {
    try {
      const res = await axios.post(`${API}/master/ai-key-pool/clear-expired`, {}, authHeader());
      toast.success(`${res.data?.removed || 0} expirada(s) removida(s)`);
      fetchPool();
    } catch (e) {
      console.error('Erro ao limpar expiradas:', e);
      toast.error('Erro ao limpar histórico');
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return '—'; }
  };

  const summary = data?.summary || {};
  const active = data?.active || [];
  const expired = data?.expired || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-2">
            <KeyRound className="text-yellow-400" size={28} /> Chaves da IA (Pede é Fácil)
          </h1>
          <p className="text-gray-400 mt-1">
            Pool de chaves Emergent que abastece a opção <strong>{'"Pede é Fácil AI (Recomendado)"'}</strong> de todos os lojistas.
            As chaves são usadas uma a uma; ao esgotar, o sistema pula para a próxima automaticamente.
          </p>
        </div>
      </div>

      {/* Resumo */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="glass-card">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-500/15"><Layers className="text-emerald-400" size={20} /></div>
            <div>
              <p className="text-gray-400 text-sm">Chaves ativas</p>
              <p className="text-2xl font-bold text-white">{summary.active ?? 0}</p>
            </div>
          </div>
        </div>
        <div className="glass-card">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-500/15"><AlertCircle className="text-red-400" size={20} /></div>
            <div>
              <p className="text-gray-400 text-sm">Chaves expiradas</p>
              <p className="text-2xl font-bold text-white">{summary.expired ?? 0}</p>
            </div>
          </div>
        </div>
        <div className="glass-card">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/15"><ShieldCheck className="text-blue-400" size={20} /></div>
            <div>
              <p className="text-gray-400 text-sm">Fallback</p>
              <p className="text-lg font-bold text-white capitalize">{summary.fallback || '—'}</p>
              {summary.using_fallback && (
                <p className="text-xs text-amber-300 mt-0.5">Pool vazio — usando chave interna</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Adicionar chaves */}
      <div className="glass-card">
        <h2 className="text-xl font-semibold text-white mb-2">Adicionar chaves</h2>
        <p className="text-sm text-gray-400 mb-3">
          Cole até <strong>100 chaves</strong> Emergent (formato <code className="text-yellow-300">sk-emergent-…</code>), uma por linha.
          Duplicadas e formatos inválidos são ignorados automaticamente.
        </p>
        <textarea
          value={keysText}
          onChange={(e) => setKeysText(e.target.value)}
          placeholder={'sk-emergent-xxxxxxxx\nsk-emergent-yyyyyyyy\nsk-emergent-zzzzzzzz'}
          rows={6}
          className="glass-input w-full font-mono text-sm resize-y"
          spellCheck={false}
        />
        <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
          <span className="text-xs text-gray-500">{lineCount} linha(s) não vazia(s)</span>
          <button
            onClick={handleSave}
            disabled={saving || !keysText.trim()}
            className="px-5 py-2.5 rounded-lg font-semibold text-black bg-yellow-400 hover:bg-yellow-300 transition disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <CheckCircle2 size={18} /> {saving ? 'Salvando...' : 'Salvar chaves'}
          </button>
        </div>
      </div>

      {/* Lista de ativas */}
      <div className="glass-card">
        <h2 className="text-xl font-semibold text-white mb-4">Chaves ativas na fila</h2>
        {loading ? (
          <div className="flex items-center justify-center py-10"><div className="loading-spinner"></div></div>
        ) : active.length === 0 ? (
          <div className="text-center py-10 text-gray-400">
            <KeyRound size={40} className="mx-auto mb-3 opacity-40" />
            <p>Nenhuma chave ativa no pool.</p>
            <p className="text-sm text-gray-500 mt-1">A IA continua funcionando pela chave interna da plataforma.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="text-gray-400 text-sm border-b border-white/10">
                  <th className="py-3 pr-4 font-medium">Chave</th>
                  <th className="py-3 px-4 font-medium">Status</th>
                  <th className="py-3 px-4 font-medium">Adicionada em</th>
                  <th className="py-3 pl-4 font-medium text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {active.map((k) => (
                  <tr key={k.id} className="border-b border-white/5 hover:bg-white/5">
                    <td className="py-3 pr-4 font-mono text-yellow-200">{k.masked}</td>
                    <td className="py-3 px-4">
                      {k.status === 'in_use' ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-500/20 text-emerald-300">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span> Em uso agora
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-500/15 text-blue-300">Na fila</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-gray-300 text-sm">{fmtDate(k.added_at)}</td>
                    <td className="py-3 pl-4 text-right">
                      <button
                        onClick={() => handleRemove(k.id)}
                        className="p-2 rounded-lg text-red-300 hover:bg-red-500/15 transition"
                        title="Remover da fila"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Histórico de expiradas */}
      <div className="glass-card">
        <button
          onClick={() => setShowExpired((s) => !s)}
          className="w-full flex items-center justify-between text-left"
        >
          <h2 className="text-xl font-semibold text-white">Histórico de expiradas ({expired.length})</h2>
          {showExpired ? <ChevronUp className="text-gray-400" /> : <ChevronDown className="text-gray-400" />}
        </button>
        {showExpired && (
          <div className="mt-4">
            {expired.length === 0 ? (
              <p className="text-center py-6 text-gray-500 text-sm">Nenhuma chave expirada ainda.</p>
            ) : (
              <>
                <div className="flex justify-end mb-3">
                  <button
                    onClick={handleClearExpired}
                    className="px-3 py-1.5 rounded-lg text-sm text-red-300 border border-red-500/30 hover:bg-red-500/10 transition flex items-center gap-1.5"
                  >
                    <Trash2 size={14} /> Limpar histórico
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="text-gray-400 text-sm border-b border-white/10">
                        <th className="py-3 pr-4 font-medium">Chave</th>
                        <th className="py-3 px-4 font-medium">Adicionada em</th>
                        <th className="py-3 pl-4 font-medium">Expirou em</th>
                      </tr>
                    </thead>
                    <tbody>
                      {expired.map((k) => (
                        <tr key={k.id} className="border-b border-white/5">
                          <td className="py-3 pr-4 font-mono text-gray-400 line-through">{k.masked}</td>
                          <td className="py-3 px-4 text-gray-400 text-sm">{fmtDate(k.added_at)}</td>
                          <td className="py-3 pl-4 text-gray-400 text-sm">{fmtDate(k.expired_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MasterAIKeyPoolContent;
